import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  alpha,
  CircularProgress,
  TableSortLabel,
  Pagination,
  Collapse,
  InputAdornment,
  Divider,
  Menu,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  Autorenew as AutorenewIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
  DeleteOutlined as DeleteOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
  PersonOutlined as PersonOutlinedIcon,
  HomeOutlined as HomeOutlinedIcon,
  LocationOnOutlined as LocationOnOutlinedIcon,
  ViewColumn as ViewColumnIcon,
} from "@mui/icons-material";
import { useGetAllHomeownersQuery } from "../../../hooks/useGetAllHomeownersQuery";
import { useDeleteHomeownerAccountMutation } from "../../../hooks/useDeleteHomeownerAccountMutation";
import { useUpdateHomeownerAccountMutation } from "../../../hooks/useUpdateHomeownerAccountMutation";
import { HomeOwnerUser } from "../../../__generated__/types";
import { searchHomeownerAccounts } from "../../../utils/search-utils";

type HomeownerWithDates = HomeOwnerUser & {
  createdAt: Date;
  updatedAt: Date;
};

// Helper function to derive account status from available fields
const getAccountStatus = (homeowner: any): 'Active' | 'Pending' | 'Inactive' => {
  const paymentStatus = homeowner?.paymentStatus?.toLowerCase();
  const subscription = homeowner?.subscription;
  // Handle emailVerified as string ('true'/'false') or boolean
  const emailVerified = homeowner?.emailVerified === 'true' || homeowner?.emailVerified === true || homeowner?.emailVerified === 'True';
  
  // Active: paid subscription and verified email
  if (paymentStatus === 'paid' && subscription && subscription !== 'Free' && emailVerified) {
    return 'Active';
  }
  
  // Inactive: cancelled or failed payment
  if (paymentStatus === 'cancelled' || paymentStatus === 'failed') {
    return 'Inactive';
  }
  
  // Pending: everything else (pending payment, unverified, free subscription)
  return 'Pending';
};

// Helper function to get account status badge styling (matches designer accounts styling)
const getAccountStatusStyle = (status: string, theme: any) => {
  switch (status) {
    case 'Active':
      return {
        bg: alpha(theme.palette.success.main, 0.10),
        color: theme.palette.success.main,
        label: 'Active',
        description: 'Account is active with successful payment'
      };
    case 'Inactive':
      return {
        bg: alpha(theme.palette.error.main, 0.10),
        color: theme.palette.error.main,
        label: 'Inactive',
        description: 'Account cancelled or payment method expired'
      };
    case 'Pending':
    default:
      return {
        bg: alpha(theme.palette.warning.main, 0.10),
        color: theme.palette.warning.dark ?? theme.palette.warning.main,
        label: 'Pending',
        description: 'Incomplete registration or payment pending'
      };
  }
};

const getDateFromObjectId = (id?: string | null): Date | null => {
  if (!id || id.length < 8) return null;
  const timestampHex = id.substring(0, 8);
  const timestamp = parseInt(timestampHex, 16);
  if (Number.isNaN(timestamp)) return null;
  return new Date(timestamp * 1000);
};

// Define available columns with visibility control
interface ColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  sortable: boolean;
  minWidth?: number;
  align?: "left" | "center" | "right";
}

const defaultColumns: ColumnConfig[] = [
  { id: "name", label: "Name", visible: true, sortable: true, minWidth: 150 },
  { id: "date", label: "Date", visible: true, sortable: true, minWidth: 120 },
  { id: "accountStatus", label: "Status", visible: true, sortable: true, minWidth: 120 },
  { id: "email", label: "Email", visible: true, sortable: true, minWidth: 200 },
  { id: "phoneNumber", label: "Phone", visible: false, sortable: true, minWidth: 120 },
  { id: "city", label: "City", visible: true, sortable: true, minWidth: 120 },
  { id: "state", label: "State", visible: true, sortable: true, minWidth: 80 },
  { id: "subscription", label: "Subscription", visible: true, sortable: true, minWidth: 120 },
  { id: "actions", label: "Actions", visible: true, sortable: false, minWidth: 200, align: "center" },
];

const STATUS_FILTERS = [
  { value: "all", label: "All Homeowners" },
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "inactive", label: "Inactive" },
];

export const HomeownerTable = () => {
  const theme = useTheme();
  const { homeowners, loading, error: queryError, refetch } = useGetAllHomeownersQuery();
  const { deleteHomeownerAccount } = useDeleteHomeownerAccountMutation();
  const { updateHomeownerAccount } = useUpdateHomeownerAccountMutation();

  // State management
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<string>("name");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Debug logging to check homeowner data
  const [selectedHomeowner, setSelectedHomeowner] = useState<HomeownerWithDates | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [columns, setColumns] = useState<ColumnConfig[]>(defaultColumns);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    homeownerId: string | null;
    homeownerName: string | null;
  }>({
    open: false,
    homeownerId: null,
    homeownerName: null,
  });
  const [error, setError] = useState<string | null>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [editFormData, setEditFormData] = useState<{
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    subscription: string;
  }>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    subscription: "",
  });

  // Helper functions
  const getRowId = (homeowner: HomeOwnerUser) => homeowner._id || "";

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setPage(1);
  };

  const handleViewHomeowner = (homeowner: HomeownerWithDates) => {
    setSelectedHomeowner(homeowner);
    setDrawerOpen(true);
  };

  const handleDeleteHomeowner = (homeownerId: string, homeownerName: string) => {
    setDeleteDialog({
      open: true,
      homeownerId,
      homeownerName,
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.homeownerId) return;

    try {
      const result = await deleteHomeownerAccount(deleteDialog.homeownerId);
      if (result) {
        await refetch();
        setDeleteDialog({
          open: false,
          homeownerId: null,
          homeownerName: null,
        });
        setError(null);
      } else {
        setError("Failed to delete homeowner account");
      }
    } catch (error: any) {
      setError(error?.message || "Failed to delete homeowner account");
    }
  };

  const handleSectionToggle = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  const handleSectionEditClick = (sectionId: string) => {
    if (selectedHomeowner) {
      setEditFormData({
        firstName: selectedHomeowner.firstName || "",
        lastName: selectedHomeowner.lastName || "",
        email: selectedHomeowner.email || "",
        phoneNumber:
          (selectedHomeowner as any).billingInfo?.phoneNumber ||
          selectedHomeowner.phoneNumber ||
          "",
        address:
          (selectedHomeowner as any).billingInfo?.address ||
          selectedHomeowner.address ||
          "",
        city:
          (selectedHomeowner as any).billingInfo?.city ||
          selectedHomeowner.city ||
          "",
        state:
          (selectedHomeowner as any).billingInfo?.state ||
          selectedHomeowner.state ||
          "",
        zipCode:
          (selectedHomeowner as any).billingInfo?.zipCode ||
          selectedHomeowner.zipCode ||
          "",
        subscription: selectedHomeowner.subscription || "",
      });
      setEditingSection(sectionId);
    }
  };

  const handleSectionEditCancel = () => {
    setEditingSection(null);
    setEditFormData({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      subscription: "",
    });
  };

  const handleEditFormChange = (field: string, value: string) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSectionEditSave = async () => {
    if (!selectedHomeowner || !editingSection) return;

    try {
      setUpdateLoading(true);
      await updateHomeownerAccount({
        variables: {
          _id: selectedHomeowner._id,
          firstName: editFormData.firstName,
          lastName: editFormData.lastName,
          email: editFormData.email,
          phoneNumber: editFormData.phoneNumber,
          address: editFormData.address,
          city: editFormData.city,
          state: editFormData.state,
          zipCode: editFormData.zipCode,
          subscription: editFormData.subscription,
        },
      });

      await refetch();
      setEditingSection(null);
      setError(null);
    } catch (error: any) {
      setError(error?.message || "Failed to update homeowner information");
    } finally {
      setUpdateLoading(false);
    }
  };

  // Column management handlers
  const handleColumnToggle = (columnId: string) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.id === columnId ? { ...col, visible: !col.visible } : col
      )
    );
  };

  const handleApplyColumns = () => {
    setColumnMenuAnchor(null);
  };

  const visibleColumns = useMemo(
    () => columns.filter((col) => col.visible),
    [columns]
  );

  // Filter and sort homeowners
  const filteredHomeowners = useMemo(() => {
    
    // First filter by status
    const statusFiltered = homeowners.filter((homeowner) => {
      const accountStatus = getAccountStatus(homeowner);
      const matchesStatus = (() => {
        switch (statusFilter) {
          case "active":
            return accountStatus === "Active";
          case "pending":
            return accountStatus === "Pending";
          case "inactive":
            return accountStatus === "Inactive";
          default:
            return true;
        }
      })();
      return matchesStatus;
    });
    

    // Then apply comprehensive search
    const searchFiltered = searchHomeownerAccounts(statusFiltered, searchTerm);

    // Sort
    searchFiltered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (orderBy) {
        case "name":
          // Check billingInfo first for real data
          const aFirstName = (a as any).billingInfo?.firstName || a.firstName || "";
          const aLastName = (a as any).billingInfo?.lastName || a.lastName || "";
          const bFirstName = (b as any).billingInfo?.firstName || b.firstName || "";
          const bLastName = (b as any).billingInfo?.lastName || b.lastName || "";
          aValue = `${aFirstName} ${aLastName}`.trim();
          bValue = `${bFirstName} ${bLastName}`.trim();
          break;
        case "date":
          aValue = getDateFromObjectId(a._id || "")?.getTime() || 0;
          bValue = getDateFromObjectId(b._id || "")?.getTime() || 0;
          break;
        case "email":
          aValue = a.email || "";
          bValue = b.email || "";
          break;
        case "phoneNumber":
          aValue = (a as any).billingInfo?.phoneNumber || (a as any).phoneNumber || "";
          bValue = (b as any).billingInfo?.phoneNumber || (b as any).phoneNumber || "";
          break;
        case "city":
          aValue = (a as any).billingInfo?.city || (a as any).city || "";
          bValue = (b as any).billingInfo?.city || (b as any).city || "";
          break;
        case "state":
          aValue = (a as any).billingInfo?.state || (a as any).state || "";
          bValue = (b as any).billingInfo?.state || (b as any).state || "";
          break;
        case "subscription":
          aValue = a.subscription || "";
          bValue = b.subscription || "";
          break;
        case "accountStatus":
          // Sort order: Active > Pending > Inactive
          const statusOrder = { Active: 3, Pending: 2, Inactive: 1 };
          aValue = statusOrder[(a as any).accountStatus as keyof typeof statusOrder] || 0;
          bValue = statusOrder[(b as any).accountStatus as keyof typeof statusOrder] || 0;
          break;
        case "emailVerified":
          aValue = a.emailVerified ? 1 : 0;
          bValue = b.emailVerified ? 1 : 0;
          break;
        default:
          aValue = a[orderBy as keyof HomeOwnerUser] || "";
          bValue = b[orderBy as keyof HomeOwnerUser] || "";
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });

    return searchFiltered;
  }, [homeowners, searchTerm, statusFilter, orderBy, order]);

  // Pagination
  const totalCount = filteredHomeowners.length;
  const pageCount = Math.ceil(totalCount / rowsPerPage);
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedHomeowners = filteredHomeowners.slice(startIndex, endIndex);

  const startRow = startIndex + 1;
  const endRow = Math.min(endIndex, totalCount);

  // Render functions
  const renderActionButton = (
    title: string,
    enabled: boolean,
    onClick: () => void,
    icon: React.ReactNode
  ) => (
    <Tooltip title={title} placement="top">
      <span>
        <IconButton
          size="small"
          disabled={!enabled}
          onClick={enabled ? onClick : undefined}
          sx={{
            borderRadius: 12,
            backgroundColor: 'transparent',
            color: enabled ? 'rgba(0, 0, 0, 0.8)' : theme.palette.grey[300],
            '&:hover': {
              backgroundColor: 'transparent',
              color: enabled ? theme.palette.grey[800] : theme.palette.grey[300],
            },
            '&.Mui-disabled': {
              color: theme.palette.grey[300],
            },
          }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  const renderCellContent = (column: ColumnConfig, homeowner: HomeOwnerUser) => {
    // Check billingInfo first for real data, then fall back to root level fields
    const firstName = (homeowner as any).billingInfo?.firstName || homeowner.firstName || "";
    const lastName = (homeowner as any).billingInfo?.lastName || homeowner.lastName || "";
    const name = `${firstName} ${lastName}`.trim() || "Unknown";

    switch (column.id) {
      case "name":
        return (
          <Typography 
            variant="body2" 
            fontWeight={500}
            sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
          >
            {name}
          </Typography>
        );
      case "date":
        return (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
          >
            {getDateFromObjectId(homeowner._id || "")?.toLocaleDateString() || "N/A"}
          </Typography>
        );
      case "accountStatus":
        const accountStatus = getAccountStatus(homeowner);
        const statusStyle = getAccountStatusStyle(accountStatus, theme);
        return (
          <Tooltip title={statusStyle.description} arrow>
            <Chip
              label={statusStyle.label}
              size="small"
              variant="filled"
              sx={{
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                height: { xs: 24, md: 28 },
                py: 0.5,
                px: 0.5,
                minWidth: 'auto',
                width: 'fit-content',
                borderRadius: 50,
                fontWeight: 500,
                backgroundColor: statusStyle.bg,
                color: statusStyle.color,
              }}
            />
          </Tooltip>
        );
      case "email":
        return (
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
          >
            {homeowner.email || "N/A"}
          </Typography>
        );
      case "phoneNumber":
        return (
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
          >
            {(homeowner as any).billingInfo?.phoneNumber || (homeowner as any).phoneNumber || "N/A"}
          </Typography>
        );
      case "city":
        return (
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
          >
            {(homeowner as any).billingInfo?.city || (homeowner as any).city || "N/A"}
          </Typography>
        );
      case "state":
        return (
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
          >
            {(homeowner as any).billingInfo?.state || (homeowner as any).state || "N/A"}
          </Typography>
        );
      case "subscription":
        return (
          <Chip
            label={homeowner.subscription || "None"}
            size="small"
            variant="outlined"
            color={homeowner.subscription ? "primary" : "default"}
            sx={{
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              height: { xs: 24, md: 28 },
              py: 0.5,
              px: 0.5,
              minWidth: 'auto',
              width: 'fit-content',
              borderRadius: 50,
            }}
          />
        );
      case "actions":
        return (
          <Stack direction="row" spacing={0.5} justifyContent="center">
            <Tooltip title="View Details">
              <IconButton
                size="small"
                onClick={() => handleViewHomeowner(homeowner as HomeownerWithDates)}
                sx={{
                  backgroundColor: 'transparent',
                  color: 'rgba(0, 0, 0, 0.8)',
                  borderRadius: 12,
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: theme.palette.grey[800],
                  },
                }}
              >
                <VisibilityOutlinedIcon />
              </IconButton>
            </Tooltip>
            {renderActionButton(
              "Delete Account",
              true,
              () => handleDeleteHomeowner(getRowId(homeowner), name),
              <DeleteOutlinedIcon />
            )}
          </Stack>
        );
      default:
        return null;
    }
  };

  const renderPaginationItem = (item: any) => (
    <Button
      {...item}
      sx={{
        minWidth: 32,
        height: 32,
        fontSize: '0.875rem',
        fontWeight: item.type === 'page' && item.page === page ? 600 : 400,
        color: item.type === 'page' && item.page === page ? 'primary.main' : 'text.secondary',
        '&:hover': {
          backgroundColor: 'primary.50',
        },
      }}
    />
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: '#00A06D' }} />
      </Box>
    );
  }

  if (queryError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Typography color="error">Error loading homeowners: {queryError.message}</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={3} sx={{ width: "100%" }}>
      <Paper
        className="admin-table-wrapper"
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 4,
          border: `1px solid ${theme.palette.grey[200]}`,
          backgroundColor: "#fcfdff",
          overflowX: "auto",
        }}
      >
        {/* Header */}
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', md: '1.75rem' } }}>
            Homeowner Accounts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '0.875rem' } }}>
            Manage all homeowner user accounts and information.
          </Typography>
        </Stack>

      {/* Filters and Search */}
      <Stack spacing={2} sx={{ mb: 2 }}>
        {/* Status Filter Chips with Horizontal Scroll */}
        <Box
          sx={{
            overflowX: 'auto',
            overflowY: 'hidden',
            '&::-webkit-scrollbar': {
              height: 4,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: theme.palette.grey[100],
              borderRadius: 2,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.grey[400],
              borderRadius: 2,
              '&:hover': {
                backgroundColor: theme.palette.grey[600],
              },
            },
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              minWidth: 'max-content',
              pb: 1,
            }}
          >
            {STATUS_FILTERS.map(({ value, label }) => (
              <Chip
                key={value}
                label={label}
                onClick={() => handleStatusFilterChange(value)}
                variant={statusFilter === value ? "filled" : "outlined"}
                color={statusFilter === value ? "primary" : "default"}
                size="small"
                sx={{
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                  height: { xs: 28, md: 32 },
                  py: 0.5, // 8px
                  px: 0.5, // 8px
                  minWidth: 'auto',
                  width: 'fit-content',
                  borderRadius: 50,
                }}
              />
            ))}
          </Stack>
        </Box>

        {/* Search and Controls - Stacked on Mobile */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 1.5, md: 2 }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent={{ xs: "flex-start", md: "space-between" }}
        >
          {/* Search Bar - Full Width on Mobile */}
          <TextField
            placeholder="Search across all data (name, email, billing, subscription, etc.)"
            value={searchTerm}
            onChange={handleSearchChange}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", md: 240 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 20,
              },
            }}
          />

          {/* Controls Row */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent={{ xs: "space-between", md: "flex-end" }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
              startIcon={<ViewColumnIcon />}
              sx={{
                borderRadius: 20,
                textTransform: "none",
                fontWeight: 600,
                fontSize: { xs: '0.75rem', md: '0.875rem' },
              }}
            >
              Columns
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AutorenewIcon />}
              onClick={() => refetch()}
              sx={{
                borderRadius: 20,
                textTransform: "none",
                fontWeight: 600,
                fontSize: { xs: '0.75rem', md: '0.875rem' },
              }}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Stack>

      {/* Table */}
      <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.grey[200]}`, borderRadius: 3 }}>
        <Box
          sx={{
            overflowX: 'auto',
            overflowY: 'hidden',
            '&::-webkit-scrollbar': {
              height: 6,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: theme.palette.grey[100],
              borderRadius: 3,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.grey[400],
              borderRadius: 3,
              '&:hover': {
                backgroundColor: theme.palette.grey[600],
              },
            },
          }}
        >
          <Table
            className="admin-dashboard-table"
            size="small"
            sx={{
              minWidth: { xs: 800, md: 'auto' },
              "& .MuiTableCell-root": {
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                whiteSpace: { xs: 'nowrap', md: 'normal' },
              },
              "& .MuiTableHead-root .MuiTableCell-root": {
                fontSize: { xs: '0.75rem', md: '0.875rem' },
              },
            }}
          >
            <TableHead>
              <TableRow>
                {visibleColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    sortDirection={orderBy === column.id ? order : false}
                    align={column.align || "left"}
                    style={{ minWidth: column.minWidth }}
                    sx={{
                      backgroundColor: theme.palette.grey[50],
                      fontWeight: 600,
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    {column.sortable ? (
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : "asc"}
                        onClick={() => handleSort(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedHomeowners.map((homeowner) => (
                <TableRow
                  key={getRowId(homeowner)}
                  hover
                  sx={{
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  {visibleColumns.map((column) => (
                    <TableCell key={column.id} align={column.align || "left"}>
                      {renderCellContent(column, homeowner)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      {/* Pagination */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 3 }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
        >
          Showing {startRow}-{endRow} of {totalCount.toLocaleString()} homeowners
        </Typography>
        <Pagination
          shape="rounded"
          count={pageCount}
          page={page}
          onChange={handlePageChange}
          siblingCount={0}
          boundaryCount={1}
          size="small"
          renderItem={renderPaginationItem}
          sx={{
            '& .MuiPagination-ul': {
              gap: { xs: 0.25, md: 0.75 },
            },
            '& .MuiPaginationItem-root': {
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              minWidth: { xs: 28, md: 32 },
              height: { xs: 28, md: 32 },
            },
          }}
        />
      </Stack>
      </Paper>

      {/* Column Selection Menu */}
      <Menu
        anchorEl={columnMenuAnchor}
        open={Boolean(columnMenuAnchor)}
        onClose={() => setColumnMenuAnchor(null)}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" gutterBottom>
            Select Columns
          </Typography>
          <FormGroup>
            {columns
              .filter((col) => col.id !== "actions")
              .map((column) => (
                <FormControlLabel
                  key={column.id}
                  control={
                    <Checkbox
                      checked={column.visible}
                      onChange={() => handleColumnToggle(column.id)}
                      size="small"
                    />
                  }
                  label={column.label}
                />
              ))}
          </FormGroup>
          <Divider sx={{ my: 1 }} />
          <Button variant="contained" size="small" fullWidth onClick={handleApplyColumns}>
            Apply
          </Button>
        </Box>
      </Menu>

      {/* Homeowner Details Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 480 },
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        {selectedHomeowner && (
          <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
            {/* Drawer Header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}
              >
                {`${selectedHomeowner.firstName || ""} ${selectedHomeowner.lastName || ""}`.trim() || "Unknown Homeowner"}
              </Typography>
              <IconButton
                onClick={() => setDrawerOpen(false)}
                sx={{ color: theme.palette.grey[600] }}
              >
                <ExpandLessIcon />
              </IconButton>
            </Stack>

            {/* Error Display */}
            {error && (
              <Box sx={{ mb: 2, p: 2, backgroundColor: theme.palette.error.light, borderRadius: 2 }}>
                <Typography variant="body2" color="error">
                  {error}
                </Typography>
              </Box>
            )}

            {/* Homeowner Information Sections */}
            <Stack spacing={2}>
              {/* Basic Information */}
              <Box
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.grey[200]}`,
                  overflow: "hidden",
                }}
              >
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSectionToggle("basic")}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleSectionToggle("basic");
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2.25,
                    py: 1.75,
                    cursor: "pointer",
                    backgroundColor: expandedSection === "basic"
                      ? alpha(theme.palette.primary.main, 0.06)
                      : theme.palette.common.white,
                    transition: "background-color 0.2s ease",
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    },
                    outline: "none",
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: theme.palette.primary.main,
                      }}
                    >
                      <PersonOutlinedIcon fontSize="small" />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Basic Information
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {expandedSection === "basic" && (
                      <Tooltip title="Edit this section">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSectionEditClick("basic");
                          }}
                          sx={{
                            backgroundColor: 'transparent',
                            color: 'rgba(0, 0, 0, 0.8)',
                            borderRadius: 12,
                            '&:hover': {
                              backgroundColor: 'transparent',
                              color: theme.palette.grey[800],
                            },
                          }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {expandedSection === "basic" ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Stack>
                </Box>
                <Collapse in={expandedSection === "basic"} unmountOnExit>
                  <Box sx={{ px: 2.25, py: 2 }}>
                    {editingSection === "basic" ? (
                      <Stack spacing={2}>
                        <Stack direction="row" spacing={2}>
                          <TextField
                            label="First Name"
                            value={editFormData.firstName}
                            onChange={(e) => handleEditFormChange("firstName", e.target.value)}
                            fullWidth
                            size="small"
                          />
                          <TextField
                            label="Last Name"
                            value={editFormData.lastName}
                            onChange={(e) => handleEditFormChange("lastName", e.target.value)}
                            fullWidth
                            size="small"
                          />
                        </Stack>
                        <TextField
                          label="Email"
                          type="email"
                          value={editFormData.email}
                          onChange={(e) => handleEditFormChange("email", e.target.value)}
                          fullWidth
                          size="small"
                        />
                        <TextField
                          label="Phone Number"
                          value={editFormData.phoneNumber}
                          onChange={(e) => handleEditFormChange("phoneNumber", e.target.value)}
                          fullWidth
                          size="small"
                        />
                        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                          <Button
                            variant="contained"
                            onClick={handleSectionEditSave}
                            disabled={updateLoading}
                            size="small"
                            sx={{ flex: 1 }}
                          >
                            {updateLoading ? "Saving..." : "Save Changes"}
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={handleSectionEditCancel}
                            disabled={updateLoading}
                            size="small"
                            sx={{ flex: 1 }}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      </Stack>
                    ) : (
                      <Stack spacing={2}>
                        <Stack direction="row" spacing={2}>
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              First Name
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                              {selectedHomeowner.firstName || "N/A"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              Last Name
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                              {selectedHomeowner.lastName || "N/A"}
                            </Typography>
                          </Box>
                        </Stack>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Email
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {selectedHomeowner.email || "N/A"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Phone Number
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {(selectedHomeowner as any).billingInfo?.phoneNumber ||
                              selectedHomeowner.phoneNumber ||
                              "N/A"}
                          </Typography>
                        </Box>
                      </Stack>
                    )}
                  </Box>
                </Collapse>
              </Box>

              {/* Location Information */}
              <Box
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.grey[200]}`,
                  overflow: "hidden",
                }}
              >
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSectionToggle("location")}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleSectionToggle("location");
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2.25,
                    py: 1.75,
                    cursor: "pointer",
                    backgroundColor: expandedSection === "location"
                      ? alpha(theme.palette.primary.main, 0.06)
                      : theme.palette.common.white,
                    transition: "background-color 0.2s ease",
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    },
                    outline: "none",
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: theme.palette.primary.main,
                      }}
                    >
                      <LocationOnOutlinedIcon fontSize="small" />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Location Information
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {expandedSection === "location" && (
                      <Tooltip title="Edit this section">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSectionEditClick("location");
                          }}
                          sx={{
                            backgroundColor: 'transparent',
                            color: 'rgba(0, 0, 0, 0.8)',
                            borderRadius: 12,
                            '&:hover': {
                              backgroundColor: 'transparent',
                              color: theme.palette.grey[800],
                            },
                          }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {expandedSection === "location" ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Stack>
                </Box>
                <Collapse in={expandedSection === "location"} unmountOnExit>
                  <Box sx={{ px: 2.25, py: 2 }}>
                    {editingSection === "location" ? (
                      <Stack spacing={2}>
                        <TextField
                          label="Address"
                          value={editFormData.address}
                          onChange={(e) => handleEditFormChange("address", e.target.value)}
                          fullWidth
                          size="small"
                        />
                        <Stack direction="row" spacing={2}>
                          <TextField
                            label="City"
                            value={editFormData.city}
                            onChange={(e) => handleEditFormChange("city", e.target.value)}
                            fullWidth
                            size="small"
                          />
                          <TextField
                            label="State"
                            value={editFormData.state}
                            onChange={(e) => handleEditFormChange("state", e.target.value)}
                            fullWidth
                            size="small"
                          />
                        </Stack>
                        <TextField
                          label="Zip Code"
                          value={editFormData.zipCode}
                          onChange={(e) => handleEditFormChange("zipCode", e.target.value)}
                          fullWidth
                          size="small"
                        />
                        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                          <Button
                            variant="contained"
                            onClick={handleSectionEditSave}
                            disabled={updateLoading}
                            size="small"
                            sx={{ flex: 1 }}
                          >
                            {updateLoading ? "Saving..." : "Save Changes"}
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={handleSectionEditCancel}
                            disabled={updateLoading}
                            size="small"
                            sx={{ flex: 1 }}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      </Stack>
                    ) : (
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Address
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {(selectedHomeowner as any).billingInfo?.address ||
                              selectedHomeowner.address ||
                              "N/A"}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={2}>
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              City
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                              {(selectedHomeowner as any).billingInfo?.city ||
                                selectedHomeowner.city ||
                                "N/A"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              State
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                              {(selectedHomeowner as any).billingInfo?.state ||
                                selectedHomeowner.state ||
                                "N/A"}
                            </Typography>
                          </Box>
                        </Stack>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Zip Code
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {(selectedHomeowner as any).billingInfo?.zipCode ||
                              selectedHomeowner.zipCode ||
                              "N/A"}
                          </Typography>
                        </Box>
                      </Stack>
                    )}
                  </Box>
                </Collapse>
              </Box>

              {/* Account Information */}
              <Box
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.grey[200]}`,
                  overflow: "hidden",
                }}
              >
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSectionToggle("account")}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleSectionToggle("account");
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2.25,
                    py: 1.75,
                    cursor: "pointer",
                    backgroundColor: expandedSection === "account"
                      ? alpha(theme.palette.primary.main, 0.06)
                      : theme.palette.common.white,
                    transition: "background-color 0.2s ease",
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    },
                    outline: "none",
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: theme.palette.primary.main,
                      }}
                    >
                      <HomeOutlinedIcon fontSize="small" />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Account Information
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {expandedSection === "account" && (
                      <Tooltip title="Edit this section">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSectionEditClick("account");
                          }}
                          sx={{
                            backgroundColor: 'transparent',
                            color: 'rgba(0, 0, 0, 0.8)',
                            borderRadius: 12,
                            '&:hover': {
                              backgroundColor: 'transparent',
                              color: theme.palette.grey[800],
                            },
                          }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {expandedSection === "account" ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Stack>
                </Box>
                <Collapse in={expandedSection === "account"} unmountOnExit>
                  <Box sx={{ px: 2.25, py: 2 }}>
                    {editingSection === "account" ? (
                      <Stack spacing={2}>
                        <TextField
                          label="Subscription"
                          value={editFormData.subscription}
                          onChange={(e) => handleEditFormChange("subscription", e.target.value)}
                          fullWidth
                          size="small"
                        />
                        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                          <Button
                            variant="contained"
                            onClick={handleSectionEditSave}
                            disabled={updateLoading}
                            size="small"
                            sx={{ flex: 1 }}
                          >
                            {updateLoading ? "Saving..." : "Save Changes"}
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={handleSectionEditCancel}
                            disabled={updateLoading}
                            size="small"
                            sx={{ flex: 1 }}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      </Stack>
                    ) : (
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Subscription
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {selectedHomeowner.subscription || "None"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Account Status
                          </Typography>
                          {(() => {
                            const accountStatus = getAccountStatus(selectedHomeowner);
                            const statusStyle = getAccountStatusStyle(accountStatus, theme);
                            return (
                              <Box>
                                <Chip
                                  label={statusStyle.label}
                                  size="small"
                                  variant="filled"
                                  sx={{
                                    py: 0.5,
                                    px: 0.5,
                                    minWidth: 'auto',
                                    width: 'fit-content',
                                    borderRadius: 50,
                                    fontWeight: 500,
                                    backgroundColor: statusStyle.bg,
                                    color: statusStyle.color,
                                  }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                  {statusStyle.description}
                                </Typography>
                              </Box>
                            );
                          })()}
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Email Verified
                          </Typography>
                          <Chip
                            label={selectedHomeowner.emailVerified ? "Verified" : "Unverified"}
                            size="small"
                            color={selectedHomeowner.emailVerified ? "success" : "warning"}
                            variant="outlined"
                            sx={{ 
                              py: 0.5, // 8px
                              px: 0.5, // 8px
                              minWidth: 'auto',
                              width: 'fit-content',
                              borderRadius: 50 
                            }}
                          />
                        </Box>
                      </Stack>
                    )}
                  </Box>
                </Collapse>
              </Box>
            </Stack>

            {/* Delete Button */}
            <Divider sx={{ my: 3 }} />
            <Button
              fullWidth
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlinedIcon />}
              onClick={() => handleDeleteHomeowner(getRowId(selectedHomeowner), `${selectedHomeowner.firstName || ""} ${selectedHomeowner.lastName || ""}`.trim() || "Unknown")}
              sx={{ borderRadius: 3, textTransform: "none", fontWeight: 600 }}
            >
              Delete Homeowner Account
            </Button>
          </Box>
        )}
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, homeownerId: null, homeownerName: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Homeowner Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the homeowner account for{" "}
            <strong>{deleteDialog.homeownerName}</strong>? This action cannot be undone and will permanently remove all associated data.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, homeownerId: null, homeownerName: null })}
            color="primary"
          >
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete Account
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
